import React, { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./AuthProvider";
import { useClient } from "./ClientProvider";
import { getAppSettingsConfig } from "../components/Logs/common/service/dataOperations";
import { resolveAppConfig, DEFAULT_APP_CONFIG, AppSettingsConfig, RemoteAppConfig } from "../services/AppConfig";

const AppConfigContext = createContext<AppSettingsConfig>(DEFAULT_APP_CONFIG);

export type Props = {
    children: React.ReactNode;
};

// Global, session-shared config (feature flags, upload limits) — resolved once here rather
// than per-consumer, since the value is identical for every consumer in the session. Mounts
// near the app root so the fetch fires as soon as clientAPIURL resolves, ahead of any consumer
// mounting; no separate prefetch call needed (unlike kpiConfig, which has no Context provider).
const AppConfigProvider = (props: Props) => {
    const { getAccessToken } = useAuth();
    const { clientAPIURL } = useClient();

    const { data: remoteAppConfig } = useQuery<RemoteAppConfig | null>({
        queryKey: ['appSettings'],
        queryFn: async (): Promise<RemoteAppConfig | null> => getAppSettingsConfig(clientAPIURL, await getAccessToken()),
        enabled: !!clientAPIURL,
        staleTime: 30 * 60 * 1000,
    });

    const appConfig = useMemo(() => resolveAppConfig(remoteAppConfig), [remoteAppConfig]);

    return (
        <AppConfigContext.Provider value={appConfig}>
            {props.children}
        </AppConfigContext.Provider>
    );
}
const useAppConfig = () => useContext(AppConfigContext);

export { AppConfigProvider, useAppConfig };
